// SprintManagement.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tooltip,
  CircularProgress,
  Stack,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  CheckCircle as CompleteIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { format } from 'date-fns';

// Mock data for sprints
const initialSprints = [
  {
    id: 1,
    name: 'Sprint Alpha',
    startDate: '2024-01-01',
    endDate: '2024-01-15',
    ticketCount: 12,
    progress: 75,
    status: 'ACTIVE',
    goal: 'Complete user authentication feature'
  },
  {
    id: 2,
    name: 'Sprint Beta',
    startDate: '2024-02-01',
    endDate: '2024-02-15',
    ticketCount: 8,
    progress: 0,
    status: 'FUTURE',
    goal: 'Implement dashboard analytics'
  },
  {
    id: 3,
    name: 'Sprint Gamma',
    startDate: '2023-12-01',
    endDate: '2023-12-15',
    ticketCount: 15,
    progress: 100,
    status: 'COMPLETED',
    goal: 'Initial project setup'
  },
  {
    id: 4,
    name: 'Sprint Delta',
    startDate: '2024-03-01',
    endDate: '2024-03-15',
    ticketCount: 10,
    progress: 30,
    status: 'ACTIVE',
    goal: 'Develop payment integration'
  },
  {
    id: 5,
    name: 'Sprint Epsilon',
    startDate: '2024-04-01',
    endDate: '2024-04-15',
    ticketCount: 7,
    progress: 0,
    status: 'FUTURE',
    goal: 'Create reporting module'
  }
];

// Custom Progress Bar Component
const ProgressBar = ({ value, max = 100, color = 'primary' }) => {
  const percentage = Math.round((value / max) * 100);

  return (
    <Tooltip title={`${percentage}%`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: '100%',
            height: 8,
            backgroundColor: 'grey.300',
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: `${percentage}%`,
              height: '100%',
              backgroundColor: `${color}.main`,
              transition: 'width 0.3s ease',
            }}
          />
        </Box>
        <Typography variant="caption" sx={{ minWidth: 30 }}>
          {percentage}%
        </Typography>
      </Box>
    </Tooltip>
  );
};

// Sprint Row Component
const SprintRow = ({ sprint, onEdit, onComplete, onDelete, onView }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'FUTURE': return 'warning';
      case 'COMPLETED': return 'default';
      default: return 'default';
    }
  };

  return (
    <TableRow hover>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={sprint.status}
            color={getStatusColor(sprint.status)}
            size="small"
            variant="outlined"
          />
          <span>{sprint.name}</span>
        </Stack>
      </TableCell>
      <TableCell>{format(new Date(sprint.startDate), 'MMM dd, yyyy')}</TableCell>
      <TableCell>{format(new Date(sprint.endDate), 'MMM dd, yyyy')}</TableCell>
      <TableCell>{sprint.ticketCount}</TableCell>
      <TableCell>
        <ProgressBar 
          value={sprint.progress} 
          max={100} 
          color={sprint.status === 'COMPLETED' ? 'grey' : 'primary'} 
        />
      </TableCell>
      <TableCell>
        <Tooltip title="Actions">
          <IconButton size="small" onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
        
        {/* Action Menu */}
        {open && (
          <Paper 
            sx={{ 
              position: 'absolute',
              zIndex: 1300,
              mt: 1,
              py: 0.5,
              boxShadow: 3
            }}
          >
            <MenuItem onClick={() => { handleMenuClose(); onEdit(sprint); }}>
              <EditIcon fontSize="small" sx={{ mr: 1 }} />
              Edit
            </MenuItem>
            {sprint.status === 'ACTIVE' && (
              <MenuItem onClick={() => { handleMenuClose(); onComplete(sprint); }}>
                <CompleteIcon fontSize="small" sx={{ mr: 1 }} />
                Complete
              </MenuItem>
            )}
            <MenuItem onClick={() => { handleMenuClose(); onView(sprint); }}>
              <ViewIcon fontSize="small" sx={{ mr: 1 }} />
              View Details
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); onDelete(sprint); }} sx={{ color: 'error.main' }}>
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              Delete
            </MenuItem>
          </Paper>
        )}
      </TableCell>
    </TableRow>
  );
};

// New Sprint Modal
const NewSprintModal = ({ open, onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: '',
    goal: '',
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.endDate) newErrors.endDate = 'End date is required';
    if (formData.endDate && formData.startDate && 
        new Date(formData.endDate) < new Date(formData.startDate)) {
      newErrors.endDate = 'End date must be after start date';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    
    const newSprint = {
      id: Date.now(),
      ...formData,
      ticketCount: 0,
      progress: 0,
      status: new Date(formData.startDate) <= new Date() ? 'ACTIVE' : 'FUTURE'
    };
    
    onCreate(newSprint);
    onClose();
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: '',
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Sprint</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Create a new sprint with the details below.
        </DialogContentText>
        
        <Stack spacing={2}>
          <TextField
            autoFocus
            required
            label="Sprint Name"
            fullWidth
            value={formData.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name}
          />
          
          <TextField
            required
            label="Start Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={formData.startDate}
            onChange={handleChange('startDate')}
            error={!!errors.startDate}
            helperText={errors.startDate}
          />
          
          <TextField
            required
            label="End Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={formData.endDate}
            onChange={handleChange('endDate')}
            error={!!errors.endDate}
            helperText={errors.endDate}
          />
          
          <TextField
            label="Sprint Goal"
            multiline
            rows={3}
            fullWidth
            value={formData.goal}
            onChange={handleChange('goal')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Edit Sprint Modal
const EditSprintModal = ({ open, onClose, sprint, onSave }) => {
  const [formData, setFormData] = useState({
    name: sprint?.name || '',
    startDate: sprint?.startDate || '',
    endDate: sprint?.endDate || '',
    goal: sprint?.goal || '',
  });

  const handleSubmit = () => {
    onSave({ ...sprint, ...formData });
    onClose();
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Sprint</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            label="Sprint Name"
            fullWidth
            value={formData.name}
            onChange={handleChange('name')}
          />
          
          <TextField
            label="Start Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={formData.startDate}
            onChange={handleChange('startDate')}
          />
          
          <TextField
            label="End Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={formData.endDate}
            onChange={handleChange('endDate')}
          />
          
          <TextField
            label="Sprint Goal"
            multiline
            rows={3}
            fullWidth
            value={formData.goal}
            onChange={handleChange('goal')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Confirm Complete Modal
const ConfirmCompleteModal = ({ open, onClose, sprintName, onConfirm }) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Complete Sprint</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to complete sprint "<strong>{sprintName}</strong>"?
          This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" color="primary">
          Complete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Delete Sprint Modal
const DeleteSprintModal = ({ open, onClose, sprintName, onConfirm }) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Delete Sprint</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete sprint "<strong>{sprintName}</strong>"?
          This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" color="error">
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// View Details Modal
const ViewDetailsModal = ({ open, onClose, sprint }) => {
  if (!sprint) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Sprint Details</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="h6">{sprint.name}</Typography>
          <Typography><strong>Status:</strong> {sprint.status}</Typography>
          <Typography><strong>Start Date:</strong> {format(new Date(sprint.startDate), 'MMM dd, yyyy')}</Typography>
          <Typography><strong>End Date:</strong> {format(new Date(sprint.endDate), 'MMM dd, yyyy')}</Typography>
          <Typography><strong>Tickets:</strong> {sprint.ticketCount}</Typography>
          <Typography><strong>Progress:</strong> {sprint.progress}%</Typography>
          <Typography><strong>Goal:</strong> {sprint.goal || 'No goal specified'}</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Sprint Management Component
const SprintManagement = () => {
  const [sprints, setSprints] = useState(initialSprints);
  const [filter, setFilter] = useState('active');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);
  
  // Modals state
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  
  // Current sprint for modals
  const [currentSprint, setCurrentSprint] = useState(null);

  // Filter sprints based on status and search
  const filteredSprints = sprints.filter(sprint => {
    const matchesFilter = filter === 'all' || 
                         (filter === 'active' && sprint.status === 'ACTIVE') ||
                         (filter === 'active_future' && ['ACTIVE', 'FUTURE'].includes(sprint.status)) ||
                         (filter === 'future' && sprint.status === 'FUTURE') ||
                         (filter === 'completed' && sprint.status === 'COMPLETED');
    
    const matchesSearch = sprint.name.toLowerCase().includes(search.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  // Pagination
  const paginatedSprints = filteredSprints.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Handle actions
  const handleCreateSprint = (newSprint) => {
    setSprints([...sprints, newSprint]);
  };

  const handleEditSprint = (sprint) => {
    setCurrentSprint(sprint);
    setShowEditModal(true);
  };

  const handleSaveSprint = (updatedSprint) => {
    setSprints(sprints.map(sprint => 
      sprint.id === updatedSprint.id ? updatedSprint : sprint
    ));
  };

  const handleCompleteSprint = (sprint) => {
    setCurrentSprint(sprint);
    setShowCompleteModal(true);
  };

  const handleConfirmComplete = () => {
    setSprints(sprints.map(sprint => 
      sprint.id === currentSprint.id 
        ? { ...sprint, status: 'COMPLETED', progress: 100 } 
        : sprint
    ));
    setShowCompleteModal(false);
    setCurrentSprint(null);
  };

  const handleDeleteSprint = (sprint) => {
    setCurrentSprint(sprint);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    setSprints(sprints.filter(sprint => sprint.id !== currentSprint.id));
    setShowDeleteModal(false);
    setCurrentSprint(null);
  };

  const handleViewSprint = (sprint) => {
    setCurrentSprint(sprint);
    setShowViewModal(true);
  };

  // Simulate loading
  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, [filter, search, page, rowsPerPage]);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>

      
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={2} 
          justifyContent="space-between" 
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ mb: 2 }}
        >
          <TextField
            size="small"
            placeholder="Search sprints..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
          
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            alignItems="center"
          >
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Sprint Status</InputLabel>
              <Select
                value={filter}
                label="Sprint Status"
                onChange={(e) => {
                  setFilter(e.target.value);
                  setPage(0); // Reset to first page when filter changes
                }}
              >
                <MenuItem value="all">All Sprints</MenuItem>
                <MenuItem value="active">Active Sprints</MenuItem>
                <MenuItem value="active_future">Active & Future Sprints</MenuItem>
                <MenuItem value="future">Future Sprints</MenuItem>
                <MenuItem value="completed">Completed Sprints</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowNewModal(true)}
              sx={{ minWidth: 150 }}
            >
              New Sprint
            </Button>
          </Stack>
        </Stack>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : paginatedSprints.length === 0 ? (
          <Box textAlign="center" p={4}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No sprints found
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowNewModal(true)}
            >
              Create Sprint
            </Button>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>No. of Tickets</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSprints.map((sprint) => (
                    <SprintRow
                      key={sprint.id}
                      sprint={sprint}
                      onEdit={handleEditSprint}
                      onComplete={handleCompleteSprint}
                      onDelete={handleDeleteSprint}
                      onView={handleViewSprint}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredSprints.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Paper>

      {/* Modals */}
      <NewSprintModal
        open={showNewModal}
        onClose={() => setShowNewModal(false)}
        onCreate={handleCreateSprint}
      />
      
      <EditSprintModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setCurrentSprint(null);
        }}
        sprint={currentSprint}
        onSave={handleSaveSprint}
      />
      
      <ConfirmCompleteModal
        open={showCompleteModal}
        onClose={() => {
          setShowCompleteModal(false);
          setCurrentSprint(null);
        }}
        sprintName={currentSprint?.name}
        onConfirm={handleConfirmComplete}
      />
      
      <DeleteSprintModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setCurrentSprint(null);
        }}
        sprintName={currentSprint?.name}
        onConfirm={handleConfirmDelete}
      />
      
      <ViewDetailsModal
        open={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setCurrentSprint(null);
        }}
        sprint={currentSprint}
      />
    </Container>
  );
};

export default SprintManagement;
